<template>
    <div class="resources">
        <div class="resources-header">
            <div class="title">
                <span class="icon">🔧</span>
                <h2>Machines & Tools</h2>
            </div>

            <div class="s" v-if="isFactorySupervisor || isFactoryAdmin"><button class="btn pointer" @click="openAddResourceModal">+ Add Resource</button></div>
        </div>

        <div class="fw center loader" v-if="loading">
          <loading-circle />
        </div>
        <div class="cards" v-else>
            <div v-for="resource in resources" :key="resource.id" class="card"
                :class="[resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'maintenance' : 'active',
                         { 'is-live': labRuns[resource.id] && labRuns[resource.id].status === 'live' }]">
                <div class="card-header">
                    <h3 class="card-name" v-overflow-tooltip>{{ resource.name }}</h3>
                    <span
                        @click="UpdateFacResourceStatus(resource)"
                        class="power"
                        :class="resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'is-maintenance' : 'is-active'"
                    >
                        ⏻
                    </span>
                </div>
                <div class="card-subtext" v-overflow-tooltip>
                   <span class="s text-grey">{{ resource?.sequence_value }}</span>
                </div>
                <div class="tags">
                    <span :class="['tag', resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'maintenance' : 'active']">{{ resource.resource_state_custom_list_value?.name === 'MAINTENANCE' ? 'MAINTENANCE' : 'ACTIVE' }}</span><span class="tag neutral">{{ resource?.core_material_master?.resource_type_value?.name  }}</span>
                </div>

                <!-- A test the machine's lab agent is streaming, or the last one it streamed -->
                <div v-if="labRuns[resource.id] && labRuns[resource.id].status === 'live'" class="card-footer-actions live">
                    <span class="live-tag" v-tooltip="labRuns[resource.id].test_name || 'Streaming from the lab PC'">
                        <span class="live-dot"></span>LIVE
                    </span>
                    <span class="live-test-name" v-overflow-tooltip>{{ labRuns[resource.id].test_name }}</span>
                    <button class="btn-watch-live pointer" @click.stop="openLabRun(resource, labRuns[resource.id])"
                        v-tooltip="'Open the live test dashboard'">
                        <span class="btn-icon">▶</span>
                        <span>Watch</span>
                    </button>
                </div>
                <div v-else-if="labRuns[resource.id]" class="card-footer-actions">
                    <button
                        class="btn-last-test pointer"
                        @click.stop="openLabRun(resource, labRuns[resource.id])"
                        v-tooltip="`Open the last streamed test${labRuns[resource.id].test_name ? ': ' + labRuns[resource.id].test_name : ''}`"
                    >Last test</button>
                </div>
            </div>
        </div>

        <lab-telemetry-overlay
            v-if="overlay.open"
            :run-id="overlay.runId"
            :machine-name="overlay.machineName"
            @close="closeOverlay"
        />
        <modal :open="addResourceModalObject.open" @close="addResourceModalObject.open = false" title="Add Resource" description="Add a new machine or tool to the factory resources">
            <add-resource v-if="addResourceModalObject.open" @cancel="addResourceModalObject.open = false" @resource-added="handleResourceCreation"/>
        </modal>
    </div>
</template>

<script>
import Modal from '@/components/common/modal.vue'
import AddResource from './addResource.vue'
import { GetAllResourceInstances, UpdateFactoryResources } from '@/api/apis/factory'
import { alert, success } from '@/plugins/notification'
import LoadingCircle from '@/components/common/loadingCircle.vue'
import { mapGetters } from 'vuex'
import { ResourceState, GetTenantConfigTypes } from '@/api'
import { startGlobalCursorLoader, stopGlobalCursorLoader } from '@/utils/globalCursorLoader'
import LabTelemetryOverlay from '@/components/labTelemetry/LabTelemetryOverlay.vue'
import { isEnabled as isLabTelemetryEnabled, listRunsForMachines } from '@/components/labTelemetry/telemetryApi'

// How often the cards check whether a rig has started or finished a test.
const LAB_RUN_POLL_MS = 10000

export default {
  name: 'resources',
  components: {
    Modal,
    AddResource,
    LoadingCircle,
    LabTelemetryOverlay
  },
  data () {
    return {
      addResourceModalObject: {
        open: false
      },
      resources: [],
      loading: false,
      // Latest run streamed by each machine's lab agent, keyed by resource ID.
      labRuns: {},
      overlay: {
        open: false,
        runId: null,
        machineName: ''
      }
    }
  },
  computed: {
    ...mapGetters(['isFactorySupervisor', 'isFactoryAdmin'])
  },
  watch: {
    // The open dashboard lives in the URL (?telemetry=<run id>), so a live test
    // can be shared as a link and the browser's Back button closes it.
    '$route.query.telemetry' () {
      this.syncOverlayFromRoute()
    }
  },
  created () {
    this.labRunsTimer = null
  },
  mounted () {
    this.init()
    this.syncOverlayFromRoute()
    if (isLabTelemetryEnabled()) {
      this.labRunsTimer = setInterval(() => this.loadLabRuns(true), LAB_RUN_POLL_MS)
    }
  },
  beforeDestroy () {
    if (this.labRunsTimer) clearInterval(this.labRunsTimer)
  },
  methods: {
    async openAddResourceModal () {
      startGlobalCursorLoader()
      try {
        const res = await GetTenantConfigTypes()
        const sequenceTemplateDefault = res?.tenant_defaults?.[0]?.tenant_feature_configuration?.FACTORIES?.RESOURCES?.SEQUENCE_TEMPLATE_DEFAULT

        if (!sequenceTemplateDefault) {
          alert('No sequence Id is associated with your tenant')
          return
        }

        this.addResourceModalObject.open = true
      } catch (err) {
        alert('Failed to validate tenant configuration')
      } finally {
        stopGlobalCursorLoader()
      }
    },
    init () {
      this.loading = true
      GetAllResourceInstances()
        .then(response => {
          this.resources = response.factory_resource_association
          return this.loadLabRuns(false)
        })
        .catch(() => {
          alert('Failed to fetch resources')
        }).finally(() => {
          this.loading = false
        })
    },
    async UpdateFacResourceStatus (resource) {
      try {
        const res = await ResourceState()
        const listValues = res?.custom_list_values || []
        const currentState = resource.resource_state_custom_list_value?.name
        const targetStateName = currentState === 'MAINTENANCE' ? 'RUNNING' : 'MAINTENANCE'
        const targetStateObj = listValues.find(item => item.name === targetStateName)
        const updateObj = {
          resource_state: targetStateObj.id
        }
        await UpdateFactoryResources(resource.id, updateObj)
        success(`Resource set to ${targetStateName === 'RUNNING' ? 'ACTIVE' : 'MAINTENANCE'}`)
        this.init()
      } catch (err) {
        console.error('Update failed:', err)
        alert('Failed to update resource status')
      }
    },
    // Finds the runs the lab agents have streamed for these machines. A rig
    // that starts a test lights its card up on the next check, with no action
    // needed in DTX. `announce` reports starts and finishes seen since the
    // last check; it is off for the first load so opening the page is quiet.
    async loadLabRuns (announce) {
      if (!isLabTelemetryEnabled() || !this.resources.length) return
      try {
        const runs = await listRunsForMachines(this.resources.map(r => r.id))
        const byResource = {}
        // Runs arrive newest first; a live run outranks a newer finished one.
        runs.forEach(run => {
          const current = byResource[run.equipment_id]
          if (!current || (current.status !== 'live' && run.status === 'live')) {
            byResource[run.equipment_id] = run
          }
        })

        if (announce) {
          this.resources.forEach(resource => {
            const was = this.labRuns[resource.id]
            const now = byResource[resource.id]
            if (now?.status === 'live' && (was?.id !== now.id || was?.status !== 'live')) {
              success(`Live test started on ${resource.name}`)
            } else if (was?.status === 'live' && now?.id === was.id && now.status === 'completed') {
              success(`Test finished on ${resource.name}. Its report is under Last test.`)
            }
          })
        }

        this.labRuns = byResource
        if (this.overlay.open && this.overlay.runId && !this.overlay.machineName) {
          this.overlay.machineName = this.machineNameForRun(this.overlay.runId)
        }
      } catch (err) {
        // The telemetry service is optional; the page works without it.
        console.warn('Lab telemetry unavailable:', err)
      }
    },
    machineNameForRun (runId) {
      const resource = this.resources.find(r => this.labRuns[r.id]?.id === runId)
      return resource?.name || ''
    },
    openLabRun (resource, run) {
      if (this.$route.query.telemetry === run.id) return
      this.overlay.machineName = resource.name
      this.$router.push({ query: { ...this.$route.query, telemetry: run.id } }).catch(() => {})
    },
    syncOverlayFromRoute () {
      const runId = this.$route.query.telemetry
      if (runId) {
        if (this.overlay.open && this.overlay.runId === runId) return
        this.overlay = {
          open: true,
          runId,
          machineName: this.overlay.machineName || this.machineNameForRun(runId)
        }
      } else if (this.overlay.open) {
        this.overlay = { open: false, runId: null, machineName: '' }
      }
    },
    closeOverlay () {
      if (this.$route.query.telemetry) {
        const query = { ...this.$route.query }
        delete query.telemetry
        this.$router.replace({ query }).catch(() => {})
      }
      this.overlay = { open: false, runId: null, machineName: '' }
    },
    handleResourceCreation () {
      this.addResourceModalObject.open = false
      this.init()
    }
  }
}
</script>

<style scoped lang="scss">
.resources {
    &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        .title {
            display: flex;
            align-items: center;
            gap: 6px;

            h2 {
                font-size: var(--text-base);
                font-weight: 400;
            }
        }
    }
}

.loader {
  height: calc(100vh - 312px);
}

.cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 14px;
    overflow-y: auto;
    max-height: calc(100vh - 312px);

    .card {
        background: #ffffff;
        border-radius: 8px;
        padding: 12px 14px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-left: 3px solid transparent;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

        &:hover {
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
        }

        &.active {
            border-left-color: #22c55e;
        }

        &.maintenance {
            border-left-color: #ef4444;
        }

        &.is-live {
            border-left-color: #dc2626;
            box-shadow: 0 0 0 1px rgba(220, 38, 38, 0.18), 0 2px 8px rgba(220, 38, 38, 0.12);
        }

        &-name {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            max-width: 160px;
        }

        &-subtext {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            max-width: 160px;
        }

        &-header {
            display: flex;
            justify-content: space-between;
            align-items: center;

            h3 {
                font-size: var(--text-base);
                font-weight: 400;
            }

            .power {
                font-size: var(--text-xl);
                opacity: 0.7;
                cursor: pointer;
                transition: opacity 0.2s, color 0.2s;
                &.is-active {
                    color: #22c55e; // Green
                }
                &.is-maintenance {
                    color: #ef4444; // Red
                }
                &:hover {
                    opacity: 1;
                }
            }
        }

        .tags {
            display: flex;
            gap: 6px;
            margin: 8px 0 12px;

            .tag {
                font-size: var(--text-xs2);
                padding: 3px 6px;
                border-radius: 5px;
                font-weight: 600;

                &.active {
                    background: #dcfce7;
                    color: #166534;
                }

                &.maintenance {
                    background: #fee2e2;
                    color: #991b1b;
                }

                &.neutral {
                    background: #f1f5f9;
                    color: #334155;
                }
            }
        }

        .card-footer-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 6px;
            padding-top: 6px;
            border-top: 1px dashed #e2e8f0;

            .btn-last-test {
                padding: 4px 8px;
                font-size: 11px;
                font-weight: 600;
                color: #1e3a5f;
                background: #f1f5f9;
                border: 1px solid #cbd5e1;
                border-radius: 5px;
                transition: all 0.15s ease;

                &:hover {
                    background: #1e3a5f;
                    border-color: #1e3a5f;
                    color: #ffffff;
                }
            }

            // A rig streaming a test: the card announces it without being opened.
            &.live {
                gap: 8px;
                border-top: 1px solid #fecaca;
            }

            .live-tag {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 2px 7px;
                font-size: 10px;
                font-weight: 700;
                letter-spacing: 0.6px;
                color: #ffffff;
                background: #dc2626;
                border-radius: 4px;
                flex-shrink: 0;
            }

            .live-dot {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background: #ffffff;
                animation: card-live-pulse 1.4s ease-in-out infinite;
            }

            .live-test-name {
                flex: 1;
                min-width: 0;
                font-size: 11px;
                font-weight: 600;
                color: #334155;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .btn-watch-live {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 4px 10px;
                font-size: 11px;
                font-weight: 600;
                color: #ffffff;
                background: #dc2626;
                border: 1px solid #dc2626;
                border-radius: 5px;
                flex-shrink: 0;
                transition: background 0.15s ease;

                .btn-icon {
                    font-size: 10px;
                    line-height: 1;
                }

                &:hover {
                    background: #b91c1c;
                    border-color: #b91c1c;
                }
            }
        }
    }

    @keyframes card-live-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.35; transform: scale(0.7); }
    }

    @media (prefers-reduced-motion: reduce) {
        .card .live-dot { animation: none; }
    }
}
</style>
